"use client";
import type { AuthType } from "@/types/auth";
import { getUUID } from "./helpers/uuid";

/**
 * In-flight refresh, shared by every concurrent 401. Without this a page that
 * fires N parallel requests would send N refresh calls, and the losers would
 * replay with a token the backend has already rotated away.
 */
let inflight: Promise<string | null> | null = null;

const onAuthRoute = () =>
	typeof window !== "undefined" && window.location.pathname.startsWith("/auth");

const doRefresh = async (): Promise<string | null> => {
	// Already signed out and sitting on the login screen: refreshing again only
	// feeds a logout -> redirect -> refetch -> refresh loop.
	if (onAuthRoute()) return null;

	try {
		const response = await fetch("/api/auth/refresh", {
			method: "POST",
			headers: {
				"content-type": "application/json",
				"x-device-id": getUUID() || "",
			},
		});

		if (!response.ok) {
			await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
			localStorage.clear();
			if (!onAuthRoute()) window.location.replace("/auth/login");
			return null;
		}

		const data: AuthType = await response.json();

		return data.token.accessToken;
	} catch (error) {
		console.error("Failed to refresh token", error);
		return null;
	}
};

const refreshAccessToken = (): Promise<string | null> => {
	if (!inflight) {
		inflight = doRefresh().finally(() => {
			inflight = null;
		});
	}
	return inflight;
};

export { refreshAccessToken };
